import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  LayoutDashboard, Users, X, Settings, Bell, ChevronDown,
  Calendar, Clock, ClipboardList, CheckSquare, Briefcase,
  MessageSquare, MessagesSquare, Newspaper, Phone, BookOpen, HelpCircle, CalendarDays,
  GraduationCap, FileText, Award, Palmtree, Cog, UserCog, Mail, ShieldCheck
} from 'lucide-react';

// Configuration des menus avec sous-menus - avec clés pour le filtrage
const menuConfig = [
  {
    key: 'organisation',
    title: 'Organisation',
    icon: Briefcase,
    items: [
      { path: '/planning', label: 'Planification', icon: Calendar, appKey: 'planning' },
      { path: '/time-tracking', label: 'Suivi du temps', icon: Clock, appKey: 'time-tracking' },
      { path: '/forms', label: 'Listes de contrôle', icon: ClipboardList, appKey: 'forms' },
      { path: '/tasks', label: 'Gestion des Tâches', icon: CheckSquare, appKey: 'tasks' },
    ]
  },
  {
    key: 'communication',
    title: 'Communication',
    icon: MessageSquare,
    items: [
      { path: '/messages', label: 'Messages', icon: Mail, appKey: 'messages' },
      { path: '/chat', label: 'Chat', icon: MessagesSquare, appKey: 'chat' },
      { path: '/news', label: 'Actualités', icon: Newspaper, appKey: 'news' },
      { path: '/directory', label: 'Annuaire', icon: Phone, appKey: 'directory' },
      { path: '/knowledge', label: 'Méthodes', icon: BookOpen, appKey: 'knowledge' },
      { path: '/support', label: 'Assistance technique', icon: HelpCircle, appKey: 'support' },
      { path: '/events', label: 'Événements', icon: CalendarDays, appKey: 'events' },
    ]
  },
  {
    key: 'hr',
    title: 'Espace RH',
    icon: Users,
    items: [
      { path: '/training', label: 'Formation', icon: GraduationCap, appKey: 'training' },
      { path: '/documents', label: 'Documents', icon: FileText, appKey: 'documents' },
      { path: '/recognition', label: 'Reconnaissance', icon: Award, appKey: 'recognition' },
      { path: '/leaves', label: 'Congés', icon: Palmtree, appKey: 'leaves' },
    ]
  },
  {
    key: 'system',
    title: 'Système',
    icon: Cog,
    items: [
      { path: '/administration', label: 'Administration', icon: ShieldCheck, adminOnly: true, appKey: 'administration' },
      { path: '/notifications', label: 'Notifications', icon: Bell, badgeKey: 'notifications', appKey: 'notifications' },
      { path: '/settings', label: 'Paramètres', icon: Settings, adminOnly: true, appKey: 'settings' },
    ]
  }
];

export default function Sidebar({ onClose, unreadNotifications = 0 }) {
  const { isAdmin, user } = useAuth();
  const location = useLocation();
  
  // Get user's visible apps (null means admin with full access)
  const visibleApps = user?.visible_apps;
  
  // Check if an app is visible to the user
  const isAppVisible = (appKey) => {
    // Admins see everything
    if (isAdmin || visibleApps === null || visibleApps === undefined) {
      return true;
    }
    // Check only the specific app key - ignore parent menu keys for visibility
    return visibleApps.includes(appKey);
  };
  
  // Check if dashboard is visible
  const isDashboardVisible = () => {
    if (isAdmin || visibleApps === null || visibleApps === undefined) {
      return true;
    }
    return visibleApps.includes('dashboard');
  };
  
  // Initialiser les états d'ouverture basés sur la route actuelle
  const getInitialOpenState = () => {
    const openState = {};
    menuConfig.forEach(menu => {
      const isActive = menu.items.some(item => location.pathname === item.path);
      openState[menu.key] = isActive;
    });
    return openState;
  };

  const [openMenus, setOpenMenus] = useState(getInitialOpenState);

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isMenuActive = (menu) => {
    return menu.items.some(item => location.pathname === item.path);
  };

  const getBadgeCount = (badgeKey) => {
    if (badgeKey === 'notifications') return unreadNotifications;
    return 0;
  };

  return (
    <aside className="sidebar h-screen flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-base leading-none">Espace</h1>
            <p className="text-xs text-muted-foreground">Employés</p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="p-3 space-y-1" data-testid="sidebar-nav">
          {/* Tableau de bord - lien direct sans sous-menu */}
          {isDashboardVisible() && (
            <NavLink
              to="/dashboard"
              onClick={onClose}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 flex-shrink-0" />
              <span>Tableau de bord</span>
            </NavLink>
          )}

          {/* Menus avec sous-menus */}
          {menuConfig.map((menu) => {
            const MenuIcon = menu.icon;
            const menuActive = isMenuActive(menu);
            const isOpen = openMenus[menu.key];

            // Filtrer les éléments admin si l'utilisateur n'est pas admin
            // ET filtrer selon les visible_apps
            const visibleItems = menu.items.filter(item => {
              // Check admin permission
              if (item.adminOnly && !isAdmin) return false;
              // Check app visibility using only the item's appKey
              return isAppVisible(item.appKey);
            });
            
            // Ne pas afficher la section si aucun élément visible
            if (visibleItems.length === 0) return null;

            return (
              <div key={menu.key} className="pt-3">
                <Collapsible open={isOpen} onOpenChange={() => toggleMenu(menu.key)}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={`sidebar-nav-item w-full justify-between ${menuActive ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                      data-testid={`nav-${menu.key}-toggle`}
                    >
                      <div className="flex items-center gap-3">
                        <MenuIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-medium">{menu.title}</span>
                      </div>
                      <ChevronDown 
                        className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const badgeCount = item.badgeKey ? getBadgeCount(item.badgeKey) : 0;
                      
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                          data-testid={`nav-${item.path.slice(1)}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm flex-1">{item.label}</span>
                          {badgeCount > 0 && (
                            <Badge variant="destructive" className="h-5 min-w-[20px] px-1.5 text-xs">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </Badge>
                          )}
                        </NavLink>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t border-border flex-shrink-0">
        <p className="text-xs text-muted-foreground text-center">© 2024 Espace Employés</p>
      </div>
    </aside>
  );
}
